import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Flag } from "lucide-react";

export default function OfficialInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/interclub/official-invite/${token}`)
      .then((r) => { setData(r.data); setForm((f) => ({ ...f, name: r.data.invite.name || "" })); })
      .catch((e) => setErr(apiErr(e)));
  }, [token]);

  const accept = async () => {
    if (!data.has_account && form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post(`/interclub/official-invite/${token}/accept`, form);
      toast.success("You're all set to officiate!");
      if (refresh) await refresh();
      navigate("/officials");
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background grain px-4"><div className="text-center"><h1 className="font-heading font-bold text-2xl">Invitation unavailable</h1><p className="text-muted-foreground mt-2">{err}</p><Link to="/login"><Button className="mt-6" variant="outline">Go to login</Button></Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const { invite, club, tournament, has_account } = data;
  const color = club?.primary_color || "#007AFF";

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-5">
          {club?.logo ? <img src={club.logo} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white" style={{ background: color }}><Flag size={22} /></div>}
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Officiating invitation</p><h1 className="font-heading font-bold text-xl" data-testid="official-invite-tournament">{tournament?.name}</h1></div>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          <b className="text-foreground">{club?.name}</b> invited you to be a match official for <b className="text-foreground">{tournament?.name}</b>. You'll run the live scoring console for matches assigned to you (<span className="font-mono text-xs">{invite.email}</span>).
        </p>
        <div className="space-y-3">
          {!has_account && <div><Label>Your name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" data-testid="official-accept-name" /></div>}
          {!has_account && <div><Label>Create a password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" className="mt-1.5 h-11" data-testid="official-accept-password" /></div>}
          <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="So the front desk can reach you on match day" className="mt-1.5 h-11" data-testid="official-accept-phone" /></div>
          {has_account && <p className="text-sm rounded-lg bg-accent/50 border border-border p-3">You already have a SphereHyx account. Accept to link it and start officiating.</p>}
        </div>
        <Button onClick={accept} disabled={busy} className="w-full mt-5 h-11 text-white border-0" style={{ background: color }} data-testid="official-accept-btn">{busy ? "Setting up..." : "Accept invitation"}</Button>
      </div>
    </div>
  );
}
